import { CommonModule } from "@angular/common";

import { Component, OnInit } from "@angular/core";

import Swal from "sweetalert2";

import { GloToggleComponent } from "../../../../shared/glo-toggle/glo-toggle.component";

import { environment } from "../../../../../environments/environment";

import { ApiService } from "../../../../shared/services/api/api.service";

import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";

interface PermissionUser {
  user_id: string;
  name: string;
  email: string;
  role: string;
  access: "owner" | "admin" | "manager" | "employee";

  initials: string;
  avatar?: string | null;
  is_owner: boolean;
  is_active: boolean;

  salon_id?: string | null;
  salon_name?: string | null;
}

interface DashboardPermission {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
}

@Component({
  selector: "business-account-permissions",

  standalone: true,

  imports: [CommonModule, GloToggleComponent],

  templateUrl: "./permissions.component.html",

  styleUrl: "./permissions.component.scss",
})
export class BusinessAccountPermissionsComponent implements OnInit {
  assetUrl = environment.assetBaseUrl;

  users: PermissionUser[] = [];

  permissions: DashboardPermission[] = [];

  isLoading = false;
  isSaving = false;

  openUserId: string | null = null;

  private originalUsers: PermissionUser[] = [];

  private originalPermissions: DashboardPermission[] = [];

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadPermissions();
  }

  /* =====================================================
     Load
     ===================================================== */

  loadPermissions(): void {
    this.isLoading = true;

    this.apiService
      .request<any>(API_ENDPOINTS.owner.accountSettings.permissions, "GET")
      .subscribe({
        next: (response: any) => {
          const data = response?.data ?? response ?? {};

          this.users = Array.isArray(data?.users) ? data.users : [];

          this.permissions = Array.isArray(data?.permissions)
            ? data.permissions
            : [];

          this.storeOriginalState();

          this.isLoading = false;
        },

        error: (error: any) => {
          console.error("Unable to load permissions:", error);

          this.isLoading = false;

          this.showError("Unable to load permissions");
        },
      });
  }

  /* =====================================================
     Permissions
     ===================================================== */

  togglePermission(permission: DashboardPermission): void {
    permission.enabled = !permission.enabled;
  }

  /* =====================================================
     User access
     ===================================================== */

  toggleUserMenu(user: PermissionUser): void {
    if (user.is_owner) {
      return;
    }

    this.openUserId = this.openUserId === user.user_id ? null : user.user_id;
  }

  setUserAccess(
    user: PermissionUser,
    access: "admin" | "manager" | "employee",
  ): void {
    if (user.is_owner) {
      return;
    }

    user.access = access;

    this.openUserId = null;
  }

  getAccessLabel(access: string): string {
    switch ((access || "").toLowerCase()) {
      case "owner":
        return "Owner";

      case "admin":
        return "Admin";

      case "manager":
        return "Manager";

      default:
        return "Employee";
    }
  }

  /* =====================================================
     Save
     ===================================================== */

  handleSubmit(): void {
    if (this.isSaving) {
      return;
    }

    const permissions = this.permissions.reduce(
      (result: Record<string, boolean>, permission) => {
        result[permission.key] = permission.enabled;

        return result;
      },
      {},
    );

    const memberAccess = this.users
      .filter((user) => !user.is_owner)
      .reduce((result: Record<string, string>, user) => {
        result[user.user_id] = user.access;

        return result;
      }, {});

    const payload = {
      permissions,
      member_access: memberAccess,
    };

    this.isSaving = true;

    this.apiService
      .request<any>(
        API_ENDPOINTS.owner.accountSettings.permissions,

        "POST",

        payload,
      )
      .subscribe({
        next: (response: any) => {
          const data = response?.data ?? response ?? {};

          if (Array.isArray(data?.users)) {
            this.users = data.users;
          }

          if (Array.isArray(data?.permissions)) {
            this.permissions = data.permissions;
          }

          this.storeOriginalState();

          this.isSaving = false;

          Swal.fire({
            icon: "success",

            title: "Permissions saved successfully",

            toast: true,

            position: "top-end",

            timer: 3000,

            timerProgressBar: true,

            showConfirmButton: false,

            customClass: {
              container: "!z-[999999]",
            },
          });
        },

        error: (error: any) => {
          console.error("Unable to save permissions:", error);

          this.isSaving = false;

          this.showError(error?.error?.message || "Unable to save permissions");
        },
      });
  }

  /* =====================================================
     Cancel
     ===================================================== */

  resetChanges(): void {
    this.users = this.clone(this.originalUsers);

    this.permissions = this.clone(this.originalPermissions);

    this.openUserId = null;

    Swal.fire({
      icon: "info",

      title: "Changes cancelled",

      toast: true,

      position: "top-end",

      timer: 2000,

      timerProgressBar: true,

      showConfirmButton: false,

      customClass: {
        container: "!z-[999999]",
      },
    });
  }

  private storeOriginalState(): void {
    this.originalUsers = this.clone(this.users);

    this.originalPermissions = this.clone(this.permissions);
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private showError(message: string): void {
    Swal.fire({
      icon: "error",

      title: message,

      toast: true,

      position: "top-end",

      timer: 3500,

      timerProgressBar: true,

      showConfirmButton: false,

      customClass: {
        container: "!z-[999999]",
      },
    });
  }
}
